/** html.undoredo のグループ・パウチ移動と同じ cubic-bezier(.9, 0, .18, 1) */
export const UNDO_REDO_TRANSITION_MS = 500;

/** @deprecated 互換 alias */
export const AUTO_SCROLL_DURATION_MS = UNDO_REDO_TRANSITION_MS;

const UNDO_REDO_BEZIER = { x1: 0.9, y1: 0, x2: 0.18, y2: 1 };

function bezierComponent(t: number, p1: number, p2: number): number {
  const inv = 1 - t;
  return 3 * inv * inv * t * p1 + 3 * inv * t * t * p2 + t * t * t;
}

function bezierComponentDerivative(t: number, p1: number, p2: number): number {
  const inv = 1 - t;
  return 3 * inv * inv * p1 + 6 * inv * t * (p2 - p1) + 3 * t * t * (1 - p2);
}

/** 入力 progress (0–1) に対し、CSS cubic-bezier(.9,0,.18,1) の y を返す */
export function undoRedoEase(progress: number): number {
  const x = Math.min(1, Math.max(0, progress));
  if (x <= 0) return 0;
  if (x >= 1) return 1;

  let t = x;
  for (let i = 0; i < 10; i++) {
    const cx = bezierComponent(t, UNDO_REDO_BEZIER.x1, UNDO_REDO_BEZIER.x2) - x;
    if (Math.abs(cx) < 1e-5) break;
    const dx = bezierComponentDerivative(t, UNDO_REDO_BEZIER.x1, UNDO_REDO_BEZIER.x2);
    if (Math.abs(dx) < 1e-6) break;
    t -= cx / dx;
    t = Math.min(1, Math.max(0, t));
  }
  return bezierComponent(t, UNDO_REDO_BEZIER.y1, UNDO_REDO_BEZIER.y2);
}

export function getRevealScrollMinVisibleLeft(
  areaRect: Pick<DOMRect, 'left'>,
  layout: { isMobile: boolean; isLeftSidebarOpen: boolean },
): number {
  if (layout.isMobile || !layout.isLeftSidebarOpen) {
    return 0;
  }
  const sidebar = document.querySelector('.left-sidebar.open') as HTMLElement | null;
  if (sidebar) {
    return Math.max(0, sidebar.getBoundingClientRect().right);
  }
  return Math.max(0, areaRect.left);
}

export function getDocumentScrollElement(): HTMLElement {
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

let activeScrollAnimationId: number | null = null;

export function cancelActiveDocumentAutoScroll(): void {
  if (activeScrollAnimationId != null) {
    cancelAnimationFrame(activeScrollAnimationId);
    activeScrollAnimationId = null;
  }
}

function clampScroll(el: HTMLElement, left: number, top: number): void {
  const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
  const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
  el.scrollLeft = Math.min(maxLeft, Math.max(0, left));
  el.scrollTop = Math.min(maxTop, Math.max(0, top));
}

/**
 * プログラム的な自動スクロール（split 追従・余白復帰など）。
 * グループ移動 undo/redo と同じ 500ms · cubic-bezier(.9,0,.18,1)。
 * ドラッグ中の端スクロール・2 本指パンは即時追従のまま。
 */
export function animateDocumentScrollBy(
  dx: number,
  dy: number,
  durationMs: number = UNDO_REDO_TRANSITION_MS,
): Promise<void> {
  if (dx === 0 && dy === 0) {
    return Promise.resolve();
  }

  cancelActiveDocumentAutoScroll();
  const el = getDocumentScrollElement();
  const startLeft = el.scrollLeft;
  const startTop = el.scrollTop;
  const startTime = performance.now();

  return new Promise(resolve => {
    const tick = (now: number) => {
      const t = undoRedoEase((now - startTime) / durationMs);
      clampScroll(el, startLeft + dx * t, startTop + dy * t);
      if (now - startTime < durationMs) {
        activeScrollAnimationId = requestAnimationFrame(tick);
      } else {
        clampScroll(el, startLeft + dx, startTop + dy);
        activeScrollAnimationId = null;
        resolve();
      }
    };
    activeScrollAnimationId = requestAnimationFrame(tick);
  });
}

export interface ScrollEdgeBufferController {
  getBuffer: () => number;
  setBuffer: (value: number) => void;
  updateCalcAreaSize: () => void;
}

/**
 * スクロール端の臨時余白（--calcarea-edge-buffer）を 0 に戻す。
 * 計算エリア縮小と scroll 位置の補正を undo/redo と同じトランジションで同期。
 */
export function animateScrollEdgeBufferToZero(
  controller: ScrollEdgeBufferController,
  durationMs: number = UNDO_REDO_TRANSITION_MS,
): Promise<void> {
  const startBuf = controller.getBuffer();
  if (startBuf === 0) {
    return Promise.resolve();
  }

  cancelActiveDocumentAutoScroll();
  const el = getDocumentScrollElement();
  const startLeft = el.scrollLeft;
  const startTop = el.scrollTop;

  controller.setBuffer(0);
  controller.updateCalcAreaSize();
  void el.scrollHeight;
  const endLeft = Math.min(startLeft, Math.max(0, el.scrollWidth - el.clientWidth));
  const endTop = Math.min(startTop, Math.max(0, el.scrollHeight - el.clientHeight));

  controller.setBuffer(startBuf);
  controller.updateCalcAreaSize();
  void el.scrollHeight;

  if (startBuf === 0 && endLeft === startLeft && endTop === startTop) {
    return Promise.resolve();
  }

  const startTime = performance.now();

  return new Promise(resolve => {
    const tick = (now: number) => {
      const t = undoRedoEase((now - startTime) / durationMs);
      const buf = startBuf * (1 - t);
      controller.setBuffer(buf);
      controller.updateCalcAreaSize();
      clampScroll(
        el,
        startLeft + (endLeft - startLeft) * t,
        startTop + (endTop - startTop) * t,
      );
      if (now - startTime < durationMs) {
        activeScrollAnimationId = requestAnimationFrame(tick);
      } else {
        controller.setBuffer(0);
        controller.updateCalcAreaSize();
        clampScroll(el, endLeft, endTop);
        activeScrollAnimationId = null;
        resolve();
      }
    };
    activeScrollAnimationId = requestAnimationFrame(tick);
  });
}
